import { useState } from "react";
import { toast } from "sonner";
import { FileDown, Check, ArrowRight } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/site/motion";
import { Eyebrow, Icon } from "@/components/site/ui";
import { AssistantChat } from "@/components/site/AssistantChat";
import { LiveNews } from "@/components/site/LiveNews";
import { NewsletterSignup } from "@/components/site/NewsletterSignup";
import { ZONES, VEILLE, VEILLE_UPDATED } from "@/data/veille";
import { exportVeillePdf } from "@/lib/veillePdf";

export const VeilleStrategique = () => {
  const [active, setActive] = useState(ZONES[0].key);
  const [exporting, setExporting] = useState(false);
  const zone = ZONES.find((z) => z.key === active);

  const handleExport = async () => {
    setExporting(true);
    try { await exportVeillePdf(); toast.success("Rapport PDF généré (logo + fiches par zone)."); }
    catch { toast.error("Export impossible."); }
    finally { setExporting(false); }
  };

  return (
    <section className="relative bg-night py-24 sm:py-32 border-t border-gold/10" data-testid="veille-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <Reveal>
            <Eyebrow light>Veille Stratégique</Eyebrow>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl font-medium text-white leading-tight">
              France · Outre-mer · <em className="not-italic italic text-gold-gradient">Afrique de l'Ouest</em>
            </h2>
            <p className="mt-3 text-white/60 font-body">Données réelles · Mise à jour {VEILLE_UPDATED}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <button onClick={handleExport} disabled={exporting} data-testid="veille-export-pdf"
              className="inline-flex items-center gap-2 rounded-full border border-gold/50 px-5 py-3 text-sm font-semibold text-gold hover:bg-gold/10 transition-colors disabled:opacity-60">
              <FileDown size={16} /> {exporting ? "Génération…" : "Export PDF — Rapport complet"}
            </button>
          </Reveal>
        </div>

        {/* Zone tabs */}
        <div className="mt-10 flex flex-wrap gap-3">
          {ZONES.map((z) => (
            <button key={z.key} onClick={() => setActive(z.key)} data-testid={`veille-tab-${z.key}`}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-all ${active === z.key ? "bg-gradient-to-r from-gold to-gold-light text-navy" : "border border-white/15 text-white/70 hover:text-white"}`}>
              <span className="text-base">{z.flag}</span> {z.name}
            </button>
          ))}
        </div>

        {/* Category cards with photos */}
        <Stagger key={active} className="mt-8 grid gap-6 lg:grid-cols-3">
          {zone.categories.map((cat) => (
            <StaggerItem key={cat.key}>
              <div className="card-lumen group h-full overflow-hidden rounded-2xl border border-white/10 bg-navy/40 hover:border-gold" data-testid={`veille-card-${active}-${cat.key}`}>
                <div className="relative h-40 overflow-hidden">
                  <img src={cat.image} alt={cat.title} loading="lazy" className="h-full w-full object-cover opacity-80 transition-transform duration-[1200ms] group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
                  <span className="absolute bottom-3 left-4 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-gold to-gold-light text-navy">
                    <Icon name={cat.icon} size={20} />
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-lg text-white">{cat.title}</h3>
                  <p className="mt-2 text-sm text-gold-light font-body italic">{cat.highlight}</p>
                  <ul className="mt-4 space-y-2.5">
                    {cat.points.map((p, i) => (
                      <li key={i} className="flex gap-2 text-sm text-white/70 font-body leading-relaxed">
                        <Check size={15} className="text-gold shrink-0 mt-0.5" /> {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        {/* Live news + passerelle band */}
        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          <LiveNews country={zone.newsKey} />
          <Reveal>
            <div className="h-full rounded-2xl border border-gold/20 bg-gradient-to-br from-navy/60 to-night p-7">
              <p className="text-xs uppercase tracking-[0.2em] text-gold mb-4">{VEILLE.market.title}</p>
              <div className="space-y-4">
                {VEILLE.market.points.map((p, i) => (
                  <p key={i} className="flex gap-2 text-sm text-white/75 font-body"><ArrowRight size={15} className="text-gold shrink-0 mt-0.5" /> {p}</p>
                ))}
              </div>
              <p className="mt-6 text-xs text-white/40 font-body">Flux agrégé (Google News) — actualisé automatiquement chaque jour.</p>
            </div>
          </Reveal>
        </div>

        {/* Newsletter */}
        <Reveal className="mt-8">
          <NewsletterSignup country={zone.newsKey} />
        </Reveal>

        {/* Assistant chat */}
        <Reveal className="mt-14">
          <div className="max-w-3xl mx-auto">
            <AssistantChat />
          </div>
        </Reveal>
      </div>
    </section>
  );
};
